import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Settings as SettingsIcon, User, Bell, Lock, Palette, MapPin, ChevronRight } from 'lucide-react';
import RetargetingSettingsCard from '../components/settings/RetargetingSettingsCard';

export default function Settings() {
  const { user } = useAuth();
  const [darkMode, setDarkMode] = useState(document.documentElement.classList.contains('dark'));

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    toast.success(`${newMode ? 'Dark' : 'Light'} mode enabled`);
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account and preferences
          </p>
        </div>

        {/* Profile */}
        <Card className="rounded-sm border">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={user?.name || ''} disabled className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Input value={user?.role?.replace('_', ' ')?.toUpperCase() || ''} disabled className="rounded-sm" />
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="rounded-sm border">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Dark Mode</p>
                <p className="text-xs text-muted-foreground">Use dark theme for better visibility</p>
              </div>
              <Switch 
                checked={darkMode} 
                onCheckedChange={toggleDarkMode}
                data-testid="dark-mode-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="rounded-sm border">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Email Notifications</p>
                <p className="text-xs text-muted-foreground">Receive updates about your deals</p>
              </div>
              <Switch defaultChecked data-testid="email-notifications-toggle" />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Pipeline Alerts</p>
                <p className="text-xs text-muted-foreground">Get notified when deals move stages</p>
              </div>
              <Switch defaultChecked data-testid="pipeline-alerts-toggle" />
            </div>
          </CardContent>
        </Card>

        {/* Mileage Rate — link to dedicated page */}
        <Card className="rounded-sm border">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Mileage Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Link to="/settings/mileage" data-testid="mileage-settings-link">
              <Button variant="outline" className="rounded-sm w-full justify-between">
                <span>CRA rate, custom rate, and expense reports</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Retargeting — opt-in Facebook CAPI for Vision Deck views */}
        <RetargetingSettingsCard />

        {/* Security */}
        <Card className="rounded-sm border">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Security
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="rounded-sm" data-testid="change-password-btn">
              Change Password
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Password change feature coming soon
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
